import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, Check } from "lucide-react";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import ClusterOverviewCard from "./components/ClusterOverviewCard";
import ProgressChart from "./components/ProgressChart";
import StatisticsCard from "./components/StatisticsCard";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import {
  clusters as clustersAPI,
  customers as customersAPI,
  surveys as surveysAPI,
  users as usersAPI,
  staffAssignments as staffAssignmentsAPI,
} from "../../lib/database";
import { useAuth } from "../../contexts/AuthContext";

const HostDashboard = () => {
  const { user } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dailyProgressData, setDailyProgressData] = useState([]);
  const [phaseProgressData, setPhaseProgressData] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [phaseTab, setPhaseTab] = useState("survey"); // survey | civil | technical
  const [dashboardTab, setDashboardTab] = useState("cluster"); // cluster | progress | village

  const [villageDropdownOpen, setVillageDropdownOpen] = useState(false);
  const [villageSearch, setVillageSearch] = useState("");
  const [selectedVillages, setSelectedVillages] = useState([]);
  const villageDropdownRef = useRef(null);

  // Listen for project selection changes
  useEffect(() => {
    const updateSelectedProject = () => {
      const project = localStorage.getItem("selectedProject");
      setSelectedProject(project && project !== "all" ? project : null);
    };

    // Initial load
    updateSelectedProject();

    // Listen for storage changes (when project is selected in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === "selectedProject") {
        updateSelectedProject();
      }
    };

    // Listen for custom event (when project is selected in same tab)
    const handleProjectChange = () => {
      updateSelectedProject();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("projectChanged", handleProjectChange);

    // Poll localStorage periodically to catch changes in same tab
    const interval = setInterval(updateSelectedProject, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("projectChanged", handleProjectChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [user, selectedProject]);

  // Helper function to fetch all customers in batches (Supabase has default limits)
  const fetchAllCustomers = async (filters = {}) => {
    const allCustomers = [];
    let page = 1;
    const pageSize = 1000; // Supabase default limit
    let hasMore = true;

    while (hasMore) {
      const customers = await customersAPI.getAll({
        page,
        pageSize,
        filters,
      }).catch(() => []);
      
      if (customers && customers.length > 0) {
        allCustomers.push(...customers);
        // If we got fewer than pageSize, we've reached the end
        if (customers.length < pageSize) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }

    return allCustomers;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Filter based on user role
      let clustersData = [];
      let customersData = [];
      let teamMembersData = [];
      let totalCustomersCount = 0;
      let completedCustomersCount = 0;
      let inProgressCustomersCount = 0;

      // Base filters for counts - use selectedProject if super_admin, otherwise use user.project_id
      const projectId = user?.role === "super_admin" && selectedProject
        ? selectedProject
        : (user?.role === "project_admin" && user?.project_id ? user.project_id : null);
      
      const baseFilters = projectId ? { project_id: projectId } : {};

      if (user?.role === "super_admin") {
        // Super admin sees all data - filter by selectedProject if set
        const [
          clustersResult,
          teamMembersResult,
          totalCount,
          completedCount,
          surveyNotStartedCount
        ] = await Promise.all([
          projectId ? clustersAPI.getByProjectId(projectId) : clustersAPI.getAll(),
          projectId ? usersAPI.getByProjectId(projectId) : usersAPI.getAll(),
          customersAPI.getCount(baseFilters),
          customersAPI.getCount({
            ...baseFilters,
            status: ["technical_complete", "commissioning_complete"],
          }),
          customersAPI.getCount({ ...baseFilters, status: "survey_not_started" })
        ]);
        clustersData = clustersResult;
        // Fetch all customer data for cluster stats (fetch in batches to get all records)
        customersData = await fetchAllCustomers(baseFilters);
        teamMembersData = teamMembersResult;
        totalCustomersCount = totalCount;
        completedCustomersCount = completedCount;
        
        // Calculate in progress: total - survey_not_started - technical_complete
        inProgressCustomersCount = totalCount - surveyNotStartedCount - completedCount;
      } else if (user?.role === "project_admin" && user?.project_id) {
        // Project admin sees only their project's data
        const [
          clustersResult,
          teamMembersResult,
          totalCount,
          completedCount,
          surveyNotStartedCount
        ] = await Promise.all([
          clustersAPI.getByProjectId(user.project_id),
          usersAPI.getByProjectId(user.project_id),
          customersAPI.getCount({ project_id: user.project_id }),
          customersAPI.getCount({
            project_id: user.project_id,
            status: ["technical_complete", "commissioning_complete"],
          }),
          customersAPI.getCount({ project_id: user.project_id, status: "survey_not_started" })
        ]);
        clustersData = clustersResult;
        // Fetch all customer data for cluster stats
        customersData = await fetchAllCustomers({ project_id: user.project_id });
        teamMembersData = teamMembersResult;
        totalCustomersCount = totalCount;
        completedCustomersCount = completedCount;
        
        // Calculate in progress: total - survey_not_started - technical_complete
        inProgressCustomersCount = totalCount - surveyNotStartedCount - completedCount;
      } else {
        // Fallback: get all data
        const [
          clustersResult,
          teamMembersResult,
          totalCount,
          completedCount,
          surveyNotStartedCount
        ] = await Promise.all([
          clustersAPI.getAll(),
          usersAPI.getAll(),
          customersAPI.getCount(),
          customersAPI.getCount({
            status: ["technical_complete", "commissioning_complete"],
          }),
          customersAPI.getCount({ status: "survey_not_started" })
        ]);
        clustersData = clustersResult;
        // Fetch all customer data for cluster stats
        customersData = await fetchAllCustomers();
        teamMembersData = teamMembersResult;
        totalCustomersCount = totalCount;
        completedCustomersCount = completedCount;
        
        // Calculate in progress: total - survey_not_started - technical_complete
        inProgressCustomersCount = totalCount - surveyNotStartedCount - completedCount;
      }

      setCustomers(customersData || []);
      setTeamMembers(teamMembersData || []);
      setSurveys([]);
      setAssignments([]);

      // Fetch minimal surveys + assignments for village-level analytics
      // (project-scoped for super_admin / project_admin)
      const [
        surveysData,
        surveyAssignments,
        civilAssignments,
        technicalAssignments,
        commissioningAssignments,
      ] = await Promise.all([
        surveysAPI.getDashboardMinimal(baseFilters).catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "surveyor" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "civil" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "technical" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "commissioning" })
          .catch(() => []),
      ]);

      setSurveys(surveysData || []);
      setAssignments([
        ...(surveyAssignments || []),
        ...(civilAssignments || []),
        ...(technicalAssignments || []),
        ...(commissioningAssignments || []),
      ]);

      // Calculate statistics using accurate counts from API
      const totalCustomers = totalCustomersCount;
      const completedCustomers = completedCustomersCount;
      const inProgressCustomers = inProgressCustomersCount;
      const teamMembersCount = teamMembersData.filter(m => m.category !== "admin").length;
      
      // Calculate rates using accurate counts (show 2 decimal places)
      const completionRate =
        totalCustomers > 0
          ? ((completedCustomers / totalCustomers) * 100).toFixed(2)
          : "0.00";
      
      const inProgressRate =
        totalCustomers > 0
          ? ((inProgressCustomers / totalCustomers) * 100).toFixed(2)
          : "0.00";

      setStatistics({
        totalInstallations: {
          value: totalCustomers.toLocaleString(),
        },
        inProgressRate: {
          value: `${inProgressRate}%`,
        },
        completionRate: {
          value: `${completionRate}%`,
        },
        teamMembers: {
          value: (teamMembersCount).toString(),
        },
      });

      // Calculate cluster stats with progress
      const clustersWithStats = (clustersData || []).map((cluster) => {
        const clusterCustomers =
          customersData?.filter((c) => c.cluster_id === cluster.id) || [];

        const total = clusterCustomers.length;
        const completed = clusterCustomers.filter(
          (c) =>
            c.status === "technical_complete" ||
            c.status === "commissioning_complete",
        ).length;
        const surveyComplete = clusterCustomers.filter(
          (c) =>
            c.status === "survey_complete" ||
            c.status?.includes("survey_complete") ||
            c.status === "civil_pending" ||
            c.status?.includes("civil")
        ).length;
        const civilComplete = clusterCustomers.filter(
          (c) =>
            c.status === "civil_complete" ||
            c.status?.includes("civil_complete") ||
            c.status === "technical_pending" ||
            c.status?.includes("technical")
        ).length;
        const technicalComplete = clusterCustomers.filter(
          (c) =>
            c.status === "technical_complete" ||
            c.status?.includes("technical_complete"),
        ).length;
        const commissioningComplete = clusterCustomers.filter(
          (c) => c.status === "commissioning_complete",
        ).length;

        const overallProgress =
          total > 0 ? Math.round((completed / total) * 100) : 0;
        const clusterTeamMembers =
          teamMembersData?.filter(
            (m) => m.cluster_id === cluster.id && m.role !== "project_admin"
          ).length || 0;

        return {
          ...cluster,
          status:
            overallProgress === 100
              ? "complete"
              : total > 0
              ? "in-progress"
              : "pending",
          dailyTarget: 0, // Can be calculated based on requirements
          completed: completed,
          overallProgress: overallProgress,
          surveyComplete: surveyComplete,
          civilComplete: civilComplete,
          technicalComplete: technicalComplete,
          commissioningComplete: commissioningComplete,
          teamMembers: clusterTeamMembers,
          lastUpdated: cluster.updated_at
            ? new Date(cluster.updated_at).toLocaleString()
            : "N/A",
        };
      });

      setClusters(clustersWithStats);

      // Calculate daily progress (last 7 days)
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date.toLocaleDateString("en-US", { weekday: "short" });
      });

      const dailyProgress = last7Days.map((day) => ({
        name: day,
        target: 0, // Can be set based on requirements
        completed: 0, // Can be calculated from customer created_at dates
      }));

      setDailyProgressData(dailyProgress);

      // Calculate phase progress (simplified - can be enhanced)
      const phaseProgress = [
        {
          name: "Current",
          survey:
            customersData?.filter(
              (c) =>
                c.status === "survey_complete" || c.status?.includes("survey")
            ).length || 0,
          civil:
            customersData?.filter(
              (c) =>
                c.status === "civil_complete" || c.status?.includes("civil")
            ).length || 0,
          technical:
            customersData?.filter(
              (c) =>
                c.status === "technical_complete" ||
                c.status?.includes("technical")
            ).length || 0,
          commissioning:
            customersData?.filter(
              (c) =>
                c.status === "commissioning_complete" ||
                (c.status && String(c.status).includes("commissioning")),
            ).length || 0,
        },
      ];

      setPhaseProgressData(phaseProgress);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const normalizeVillage = (v) => {
    const s = (v || "").toString().trim();
    return s ? s : "—";
  };

  const isSameDay = (iso, day) => {
    if (!iso) return false;
    const d = new Date(iso);
    return (
      d.getFullYear() === day.getFullYear() &&
      d.getMonth() === day.getMonth() &&
      d.getDate() === day.getDate()
    );
  };

  useEffect(() => {
    if (!villageDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        villageDropdownRef.current &&
        !villageDropdownRef.current.contains(e.target)
      ) {
        setVillageDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [villageDropdownOpen]);

  const allVillages = React.useMemo(() => {
    const set = new Set();
    (customers || []).forEach((c) => set.add(normalizeVillage(c?.village)));
    (assignments || []).forEach((a) =>
      set.add(normalizeVillage(a?.customers?.village)),
    );
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [customers, assignments]);

  const filteredVillageOptions = villageSearch.trim()
    ? allVillages.filter((v) =>
        v.toLowerCase().includes(villageSearch.toLowerCase()),
      )
    : allVillages;

  const toggleVillage = (villageName) => {
    const v = normalizeVillage(villageName);
    setSelectedVillages((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  };

  const selectedVillageLabel =
    selectedVillages.length === 0
      ? "All villages"
      : selectedVillages.length === 1
        ? selectedVillages[0]
        : `${selectedVillages.length} villages`;

  const phaseConfig = {
    survey: {
      role: "surveyor",
      completed: new Set([
        "survey_complete",
        "civil_pending",
        "civil_inprogress",
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["survey_not_started", "survey_inprogress"]),
    },
    civil: {
      role: "civil",
      completed: new Set([
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["civil_pending", "civil_inprogress"]),
    },
    technical: {
      role: "technical",
      completed: new Set([
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["technical_pending", "technical_inprogress"]),
    },
    commissioning: {
      role: "commissioning",
      completed: new Set(["commissioning_complete"]),
      pending: new Set([
        "commissioning_not_started",
        "commissioning_inprogress",
      ]),
    },
  };

  const surveysByCustomerId = React.useMemo(() => {
    const map = new Map();
    (surveys || []).forEach((s) => {
      if (s?.customer_id) map.set(s.customer_id, s);
    });
    return map;
  }, [surveys]);

  const feasibleCount = React.useMemo(() => {
    let n = 0;
    (customers || []).forEach((c) => {
      const s = surveysByCustomerId.get(c.id);
      if (s?.roof_feasible === true) n += 1;
    });
    return n;
  }, [customers, surveysByCustomerId]);

  const nonFeasibleCount = React.useMemo(() => {
    let n = 0;
    (customers || []).forEach((c) => {
      const s = surveysByCustomerId.get(c.id);
      if (s?.roof_feasible === false) n += 1;
    });
    return n;
  }, [customers, surveysByCustomerId]);

  const notStartedCount = React.useMemo(() => {
    return (customers || []).filter((c) => c?.status === "survey_not_started")
      .length;
  }, [customers]);

  const villageProgressRows = React.useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const byVillage = new Map();

    (customers || []).forEach((c) => {
      const village = normalizeVillage(c?.village);
      if (!byVillage.has(village))
        byVillage.set(village, { village, completed: 0, pending: 0 });
      const row = byVillage.get(village);
      const status = c?.status || "";
      if (cfg.completed.has(status)) row.completed += 1;
      else if (cfg.pending.has(status)) row.pending += 1;
    });

    return Array.from(byVillage.values())
      .filter((r) => r.completed + r.pending > 0)
      .sort((a, b) => (b.completed + b.pending) - (a.completed + a.pending));
  }, [customers, phaseTab]);

  const todayAllocationsRows = React.useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const today = new Date();
    const byVillage = new Map();

    (assignments || [])
      .filter((a) => a?.users?.role === cfg.role)
      .forEach((a) => {
        if (!isSameDay(a?.assigned_at, today)) return;
        const village = normalizeVillage(a?.customers?.village);
        byVillage.set(village, (byVillage.get(village) || 0) + 1);
      });

    return Array.from(byVillage.entries())
      .map(([village, count]) => ({ village, count }))
      .sort((a, b) => b.count - a.count);
  }, [assignments, phaseTab]);

  const currentAllocationsRows = React.useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const byVillage = new Map();

    (assignments || [])
      .filter((a) => a?.users?.role === cfg.role)
      .filter((a) => a?.status !== "completed")
      .forEach((a) => {
        const village = normalizeVillage(a?.customers?.village);
        byVillage.set(village, (byVillage.get(village) || 0) + 1);
      });

    return Array.from(byVillage.entries())
      .map(([village, count]) => ({ village, count }))
      .sort((a, b) => b.count - a.count);
  }, [assignments, phaseTab]);

  const villageProgressByName = React.useMemo(() => {
    return new Map((villageProgressRows || []).map((r) => [r.village, r]));
  }, [villageProgressRows]);

  const displayedVillageProgressRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => {
          const row = villageProgressByName.get(v);
          return row || { village: v, completed: 0, pending: 0 };
        })
      : villageProgressRows;

  const todayAllocationsByName = React.useMemo(() => {
    return new Map((todayAllocationsRows || []).map((r) => [r.village, r.count]));
  }, [todayAllocationsRows]);

  const displayedTodayAllocationsRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => ({
          village: v,
          count: todayAllocationsByName.get(v) || 0,
        }))
      : todayAllocationsRows;

  const currentAllocationsByName = React.useMemo(() => {
    return new Map(
      (currentAllocationsRows || []).map((r) => [r.village, r.count]),
    );
  }, [currentAllocationsRows]);

  const displayedCurrentAllocationsRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => ({
          village: v,
          count: currentAllocationsByName.get(v) || 0,
        }))
      : currentAllocationsRows;

  const todayAllocationsToShow =
    selectedVillages.length > 0
      ? displayedTodayAllocationsRows
      : (todayAllocationsRows || []).slice(0, 20);

  const currentAllocationsToShow =
    selectedVillages.length > 0
      ? displayedCurrentAllocationsRows
      : (currentAllocationsRows || []).slice(0, 20);

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | Valence Energies</title>
        <meta
          name="description"
          content="Comprehensive administrative dashboard for monitoring solar installation progress across multiple clusters with team management and analytics"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />

        <main className="content-area">
          <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Admin Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Monitor installation progress, manage teams, and analyze
                performance across all clusters
              </p>
              {error && (
                <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading dashboard data...
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                  <StatisticsCard
                    title="Total Installations"
                    value={statistics?.totalInstallations?.value}
                    icon="Zap"
                  />
                  <StatisticsCard
                    title="In Progress Rate"
                    value={statistics?.inProgressRate?.value}
                    icon="Activity"
                  />
                  <StatisticsCard
                    title="Completion Rate"
                    value={statistics?.completionRate?.value}
                    icon="TrendingUp"
                  />
                  <StatisticsCard
                    title="Team Members"
                    value={statistics?.teamMembers?.value}
                    icon="Users"
                  />
                </div>

                <div className="bg-card border border-border rounded-lg p-3 md:p-4 mb-6 md:mb-8">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={dashboardTab === "cluster" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDashboardTab("cluster")}
                      iconName="Grid3X3"
                      iconPosition="left"
                    >
                      Cluster Overview
                    </Button>
                    <Button
                      variant={dashboardTab === "progress" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDashboardTab("progress")}
                      iconName="TrendingUp"
                      iconPosition="left"
                    >
                      Progress
                    </Button>
                    <Button
                      variant={dashboardTab === "village" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDashboardTab("village")}
                      iconName="Map"
                      iconPosition="left"
                    >
                      Village Analytics
                    </Button>
                  </div>
                </div>

                {dashboardTab === "cluster" && (
                  <div className="mb-6 md:mb-8">
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4 md:mb-6">
                      Cluster Overview
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                      {clusters?.map((cluster) => (
                        <ClusterOverviewCard
                          key={cluster?.id}
                          cluster={cluster}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {dashboardTab === "progress" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                    <ProgressChart
                      data={dailyProgressData}
                      type="bar"
                      title="Daily Progress - Target vs Completed"
                    />
                    <ProgressChart
                      data={phaseProgressData}
                      type="line"
                      title="Phase-wise Progress Trend"
                    />
                  </div>
                )}

                {dashboardTab === "village" && (
                  <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                        Village Analytics
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Feasible / Non-feasible / Not Started, plus village-wise
                        progress and allocations.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant={phaseTab === "survey" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPhaseTab("survey")}
                          iconName="ClipboardCheck"
                          iconPosition="left"
                        >
                          Survey
                        </Button>
                        <Button
                          variant={phaseTab === "civil" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPhaseTab("civil")}
                          iconName="HardHat"
                          iconPosition="left"
                        >
                          Civil
                        </Button>
                        <Button
                          variant={
                            phaseTab === "technical" ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setPhaseTab("technical")}
                          iconName="Wrench"
                          iconPosition="left"
                        >
                          Technical
                        </Button>
                        <Button
                          variant={
                            phaseTab === "commissioning" ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setPhaseTab("commissioning")}
                          iconName="ClipboardCheck"
                          iconPosition="left"
                        >
                          Commissioning
                        </Button>
                      </div>

                      <div
                        className="flex flex-col gap-1 relative w-full sm:w-72"
                        ref={villageDropdownRef}
                      >
                        <label className="text-sm font-medium text-foreground">
                          Villages
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setVillageDropdownOpen((o) => !o);
                            if (!villageDropdownOpen) setVillageSearch("");
                          }}
                          className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            selectedVillages.length === 0
                              ? "text-muted-foreground"
                              : "text-foreground"
                          }`}
                        >
                          <span className="truncate text-left">
                            {selectedVillageLabel}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 shrink-0 transition-transform ${
                              villageDropdownOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {villageDropdownOpen && (
                          <div className="absolute right-0 top-full z-[10000] mt-1 w-full min-w-[240px] max-w-[360px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
                            <div className="p-2 border-b border-border">
                              <input
                                type="search"
                                placeholder="Search villages..."
                                value={villageSearch}
                                onChange={(e) => setVillageSearch(e.target.value)}
                                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                autoFocus
                              />
                            </div>
                            <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                              {filteredVillageOptions.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-muted-foreground">
                                  No villages match
                                </div>
                              ) : (
                                filteredVillageOptions.map((v) => {
                                  const checked = selectedVillages.includes(v);
                                  return (
                                    <div
                                      key={v}
                                      role="option"
                                      aria-selected={checked}
                                      className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                      onClick={() => toggleVillage(v)}
                                    >
                                      <span
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                          checked
                                            ? "bg-primary border-primary text-primary-foreground"
                                            : "border-input"
                                        }`}
                                      >
                                        {checked ? (
                                          <Check className="h-3 w-3" />
                                        ) : null}
                                      </span>
                                      <span className="truncate">{v}</span>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            <div className="p-2 border-t border-border flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedVillages([])}
                                className="text-sm text-muted-foreground hover:underline"
                              >
                                Clear
                              </button>
                              <span className="text-xs text-muted-foreground">
                                {selectedVillages.length === 0
                                  ? "All"
                                  : `${selectedVillages.length} selected`}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Feasible sites
                          </p>
                          <p className="text-2xl font-semibold text-foreground">
                            {feasibleCount.toLocaleString()}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                          <Icon
                            name="CheckCircle2"
                            size={18}
                            className="text-success"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Non-feasible sites
                          </p>
                          <p className="text-2xl font-semibold text-foreground">
                            {nonFeasibleCount.toLocaleString()}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                          <Icon
                            name="XCircle"
                            size={18}
                            className="text-destructive"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Not started
                          </p>
                          <p className="text-2xl font-semibold text-foreground">
                            {notStartedCount.toLocaleString()}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                          <Icon
                            name="Clock"
                            size={18}
                            className="text-warning"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <div className="rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-foreground">
                          Village progress ({phaseTab})
                        </h3>
                        <span className="text-xs text-muted-foreground">
                          Pending vs Completed
                        </span>
                      </div>
                      <div className="max-h-[520px] overflow-auto">
                        <table className="w-full">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Village
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                Completed
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                Pending
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {displayedVillageProgressRows.map((r) => (
                              <tr key={r.village}>
                                <td className="px-3 py-2 text-sm text-foreground">
                                  {r.village}
                                </td>
                                <td className="px-3 py-2 text-sm text-foreground text-right">
                                  {r.completed.toLocaleString()}
                                </td>
                                <td className="px-3 py-2 text-sm text-foreground text-right">
                                  {r.pending.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                            {displayedVillageProgressRows.length === 0 && (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                                >
                                  No data.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Showing all villages. Scroll to view more.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border p-4 space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-foreground">
                            Today allocation ({phaseTab})
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            Assigned today
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                  Village
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                  No. of Units
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {todayAllocationsToShow.map((r) => (
                                <tr key={r.village}>
                                  <td className="px-3 py-2 text-sm text-foreground">
                                    {r.village}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-foreground text-right">
                                    {r.count.toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                              {todayAllocationsToShow.length === 0 && (
                                <tr>
                                  <td
                                    colSpan={2}
                                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                                  >
                                    No allocations today.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-foreground">
                            Current allocations ({phaseTab})
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            Active (not completed)
                          </span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                  Village
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {currentAllocationsToShow.map((r) => (
                                <tr key={r.village}>
                                  <td className="px-3 py-2 text-sm text-foreground">
                                    {r.village}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-foreground text-right">
                                    {r.count.toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                              {currentAllocationsToShow.length === 0 && (
                                <tr>
                                  <td
                                    colSpan={2}
                                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                                  >
                                    No active allocations.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default HostDashboard;
